<template>
  <div
    class="in-console-article d-flex flex-row"
    :class="{ 'in-console-article--reading_more': isReadingMore }"
  >
    <div
      class="in-console-article__icon"
      v-html="icon"
      v-if="contentToShow === 'in-console-messages' && !isReadingMore"
    />
    <div class="in-console-article__content">
      <a
        href="javascript:"
        class="in-console-article__content__return"
        v-if="isReadingMore"
        @click="toggleReadingMore"
      >
        <span v-html="returnArrow" />
        {{ t("RETURN") }}
      </a>
      <p class="in-console-article__content__title">{{ message.title }}</p>
      <p class="in-console-article__content__time">{{ messageTime }}</p>
      <div
        class="in-console-article__content__message"
        :class="{
          'in-console-article__content__message--short':
            longBody && !isReadingMore,
        }"
        v-html="parsedBody"
      />
      <a
        href="javascript:"
        class="in-console-article__content__read-more"
        v-if="longBody && !isReadingMore"
        @click="toggleReadingMore"
      >
        {{ t("READ_MORE") }}
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { IConsoleArticle } from "@/apps/merchant3/types/in-console/InConsoleArticle";
import { getLanguage, getLanguageName } from "@/apps/merchant3/shared-helpers/language.helper";
import { getRelativeTime } from "@/apps/merchant3/shared-helpers/date-helper";
import { useStore } from "vuex";
import useAsset from "@/apps/merchant3/shared-helpers/useAsset";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";

const store = useStore();
const { t } = useI18n();
const props = defineProps<{
  message: IConsoleArticle;
}>();

let isReadingMore = ref<boolean>();

const pinIcon =
  '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none"><path d="M8 7L9.5 8.5V10H5.75V14.5L5 15.25L4.25 14.5V10H0.5V8.5L2 7V1.75H1.25V0.25H8.75V1.75H8V7ZM2.6375 8.5H7.3625L6.5 7.6375V1.75H3.5V7.6375L2.6375 8.5Z" fill="#666666"/></svg>';
const redDot =
  '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none"><circle cx="5.4" cy="5.4" r="5.4" fill="#E11D48"/></svg>';
const greyDot =
  '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none"><circle cx="5.4" cy="5.4" r="5.4" fill="#D1D1D1"/></svg>';
const returnArrow =
  '<svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none"><mask id="mask0_20164_147" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="19" height="19"><rect width="19" height="19" fill="#D9D9D9"/></mask><g mask="url(#mask0_20164_147)"><path d="M6.19475 10.2916L10.6281 14.725L9.49996 15.8333L3.16663 9.49996L9.49996 3.16663L10.6281 4.27496L6.19475 8.70829H15.8333V10.2916H6.19475Z" fill="#2C7040"/></g></svg>';

const messageDate =
  props.message.date && props.message.date !== ""
    ? new Date(props.message.date)
    : null;
const nowDate = new Date();

const contentToShow = computed(
  () => store.getters["ui/sidebarState"].contentToShow
);
const icon = computed(() => {
  if (props.message.isFeatured) {
    return pinIcon;
  }

  if (!messageDate) {
    return greyDot;
  }

  const differenceMS = Math.abs(nowDate.getTime() - messageDate.getTime());
  /**
   * Two-day difference in MS for "Recent" Messages.
   * Backend doesn't support yet the unread/read messages state.
   */
  const twoDays = 48 * 60 * 60 * 1000;

  return differenceMS >= twoDays ? greyDot : redDot;
});

const longBody = computed(() => {
  const textContent = props.message.body.replace(/<[^>]*>/g, "");

  return textContent.length > 150;
});

const messageTime = computed(() => {
  if (!messageDate) {
    return "";
  }

  const differenceMS = Math.abs(nowDate.getTime() - messageDate.getTime());
  const oneDay = 24 * 60 * 60 * 1000;
  const localization = getLanguage().replace("_", "-");

  if (differenceMS >= oneDay) {
    return getRelativeTime(messageDate, getLanguageName(), true);
  }

  return messageDate.toLocaleTimeString(localization, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
});

const parsedBody = computed(() => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(props.message.body, "text/html");

  doc.querySelectorAll("img").forEach((img) => {
    const srcBinding = img.getAttribute("src");

    if (srcBinding) {
      img.setAttribute("src", useAsset(srcBinding));
    }
  });

  return doc.body.innerHTML;
});

const toggleReadingMore = () => {
  if (!isReadingMore.value) {
    logEvent(EventLogs.Events.LONG_TRAINING_ARTICLE_SHOWN, {
      articleTitle: props.message.title,
    });
  }

  isReadingMore.value = !isReadingMore.value;
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants";

.in-console-article {
  gap: 11px;
  border-bottom: 2px solid $pj-color-light-gray;
  padding: 17px 25px 33px 25px;
  z-index: 1;
  background: $pj-color-white;
  transition: all 0.5s;

  &--reading_more {
    position: absolute;
    z-index: 2;
    top: 40px;
    left: 0;
    min-height: calc(100% - 40px);
  }

  &__content {
    &__title {
      font-size: 15px;
      font-weight: 700;
      color: $pj-color-granite-gray;
      line-height: 24px;
      margin-bottom: 0;
    }

    &__time {
      font-size: 15px;
      font-weight: 500;
      color: $pj-color-gray;
      line-height: 24px;
      margin-bottom: 11px;
    }

    &__message {
      font-size: 15px;
      font-weight: 500;
      color: $pj-color-granite-gray;

      p:last-child {
        margin-bottom: 0 !important;
      }

      &--short {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 1rem;
      }

      ::v-deep(img) {
        max-width: 100%;
        margin-bottom: 1rem;

        &:last-child {
          margin-bottom: 0;
        }
      }

      img {
        max-width: 100%;
        margin-bottom: 1rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__return {
      color: $pj-color-green;
      font-size: 14px;
      text-decoration: none;
      font-weight: normal;
      height: 19.5px;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 3px;
      margin-bottom: 12px;
    }

    &__read-more {
      color: $pj-color-green;
      font-size: 15px;
      font-style: italic;
      text-decoration: none;
      font-weight: 700;
    }
  }
}
</style>
